<template>
    <div class="destination-selector">
        <!-- Special styled input for Redirect URL that looks like a v-select -->
        <div v-if="selectedDestination === 'redirect_url'" class="destination-selector__redirect-field">
            <div 
                class="destination-selector__field-wrapper"
                :class="{ 'destination-selector__field-wrapper--error': isRedirectUrlInvalid }"
            >
                <div class="destination-selector__content">
                    <div 
                        :class="[
                            'destination-icon',
                            'destination-icon--special'
                        ]"
                        class="mr-3"
                        @click="openMenu"
                        style="background-color: #F2F5FF; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; border-radius: 8px; padding: 7px; box-sizing: border-box;"
                    >
                        <component
                            :is="'ExternalLinkIcon'"
                            width="16"
                            height="16"
                        />
                    </div>
                    <span class="mr-2 ml-3" @click="openMenu">Redirect URL:</span>
                    <input
                        v-model="redirectUrl"
                        placeholder="https://example.com"
                        class="destination-selector__url-input"
                        :class="{ 'destination-selector__url-input--error': isRedirectUrlInvalid }"
                        @input="validateUrl"
                        @click.stop
                        @focus="handleUrlFocus"
                        @mousedown.stop
                        @blur="handleUrlBlur"
                        ref="urlInput"
                    />
                </div>
                <div class="destination-selector__dropdown-arrow" @click="openMenu">
                    <v-icon>mdi-chevron-down</v-icon>
                </div>
            </div>

            <!-- Manual dropdown menu -->
            <v-menu
                v-model="menuOpen"
                :close-on-content-click="false"
                :nudge-bottom="2"
                :max-height="300"
                offset-y
                content-class="conditional-logic-editor__target-items"
            >
                <template #activator="{ on, attrs }">
                    <div 
                        v-bind="attrs" 
                        v-on="on" 
                        ref="menuActivator"
                        style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; opacity: 0; pointer-events: none;"
                    ></div>
                </template>
                
                <v-list dense>
                    <v-list-item
                        v-for="item in allDestinationOptions"
                        :key="item.value"
                        :disabled="item.disabled"
                        @click="selectDestination(item)"
                        dense
                    >
                        <!-- Icons - different for special vs regular -->
                        <v-list-item-icon v-if="item.type === 'special'">
                            <!-- Special icons with styled wrapper -->
                            <div 
                                :class="[
                                    'destination-icon',
                                    'destination-icon--special'
                                ]"
                                style="background-color: #F2F5FF; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; border-radius: 8px; padding: 7px; box-sizing: border-box; margin-right: 12px;"
                            >
                                <span v-if="item.isEmoji" class="destination-icon__emoji">{{ item.icon }}</span>
                                <component
                                    v-else
                                    :is="item.icon"
                                    width="16"
                                    height="16"
                                />
                            </div>
                        </v-list-item-icon>

                        <v-list-item-icon v-else>
                            <!-- Regular question icons without wrapper -->
                            <component
                                :is="item.icon"
                                width="30"
                                height="30"
                            />
                        </v-list-item-icon>

                        <!-- Content with tooltip for questions only -->
                        <v-list-item-content :style="`max-width: ${item.disabled ? (item.disabledLabel === 'Conditioned' ? '460px' : '500px') : '533px'}; overflow: hidden;`">
                            <v-tooltip
                                v-if="item.type !== 'special'"
                                right
                                :disabled="!shouldShowTooltip(item)"
                            >
                                <template #activator="{ on, attrs }">
                                    <v-list-item-title 
                                        class="destination-selector__question-title"
                                        v-bind="attrs"
                                        v-on="on"
                                        style="display: flex; align-items: center; overflow: hidden;"
                                    >
                                        <!-- Regular questions - location part lighter -->
                                        <span 
                                            class="mr-2 grey_700--text destination-selector__question-location"
                                            style="flex-shrink: 0;"
                                        >{{ item.formattedLocation }}:</span>
                                        <span 
                                            class="destination-selector__question-text"
                                            :style="`overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: ${item.disabled ? (item.disabledLabel === 'Hidden' ? '330px' : '340px') : '433px'};`"
                                        >{{ item.title }}</span>
                                    </v-list-item-title>
                                </template>
                                <span class="tooltip-text">{{ item.formattedLocation }}: {{ item.title }}</span>
                            </v-tooltip>

                            <v-list-item-title 
                                v-else
                                class="destination-selector__question-title"
                                style="display: flex; align-items: center; overflow: hidden;"
                            >
                                <!-- Special options - all text same color -->
                                <span 
                                    class="destination-selector__question-text"
                                    :style="`overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: ${item.disabled ? (item.disabledLabel === 'Hidden' ? '330px' : '340px') : '433px'};`"
                                >{{ item.label }}</span>
                            </v-list-item-title>
                        </v-list-item-content>

                        <v-list-item-action 
                            v-if="item.disabled"
                            :style="`flex-shrink: 0; min-width: 70px; margin-left: ${item.disabledLabel === 'Conditioned' ? '24px' : '16px'};`"
                        >
                            <span class="caption text--disabled">{{ item.disabledLabel }}</span>
                        </v-list-item-action>
                    </v-list-item>
                </v-list>
            </v-menu>
        </div>

        <!-- Regular v-select for all other cases -->
        <div v-else class="destination-selector__wrapper">
            <f-select
                    v-model="selectedDestination"
                    :items="allDestinationOptions"
                    :item-value="item => item.value"
                    :item-disabled="item => item.disabled"
                    :menu-props="menuProps"
                    outlined
                    dense
                    hide-details
                    :placeholder="dynamicPlaceholder"
                    class="destination-selector__question conditional-logic-editor__target-field"
                    ref="mainSelect"
            >
                <template #selection="{item}">
                    <div style="display: flex; align-items: center; width: 100%; overflow: hidden;">
                        <!-- Special icons with styled wrapper -->
                        <div 
                            v-if="item.type === 'special'"
                            :class="[
                                'destination-icon',
                                'destination-icon--special'
                            ]"
                            style="background-color: #F2F5FF; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; border-radius: 8px; padding: 7px; box-sizing: border-box; flex-shrink: 0;"
                        >
                            <span v-if="item.isEmoji" class="destination-icon__emoji">{{ item.icon }}</span>
                            <component
                                v-else
                                :is="item.icon"
                                width="16"
                                height="16"
                            />
                        </div>
                        <!-- Regular question icons without wrapper -->
                        <component
                            v-else
                            :is="item.icon"
                            width="30"
                            height="30"
                            style="flex-shrink: 0;"
                        />
                        <!-- Text with different styling based on type -->
                        <span v-if="item.type === 'special'" style="margin-left: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1;">{{ item.label }}</span>
                        <template v-else>
                            <div style="margin-left: 12px; overflow: hidden; flex: 1; display: flex; align-items: center;">
                                <span class="mr-2 grey_700--text font-weight-bold" style="flex-shrink: 0;">{{ optionLetter }}  {{ option.isEmoji ? '' : '.' }}</span>
                                <span class="grey_700--text" style="flex-shrink: 0;"> {{ item.formattedLocation }}:</span>
                                <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1;">{{ item.title }}</span>
                            </div>
                        </template>
                    </div>
                </template>
                <template #item="{on, attrs, item}">
                    <v-list-item
                            v-bind="attrs"
                            v-on="on"
                            :disabled="item.disabled"
                            :class="{ 'v-list-item--disabled': item.disabled }"
                    >
                        <!-- Tooltip only for questions, not special options -->
                        <v-tooltip
                            v-if="item.type !== 'special'"
                            right
                            :disabled="!shouldShowTooltip(item)"
                        >
                            <template #activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                                <div
                                    v-bind="{ ...attrs, ...tooltipAttrs }"
                                    v-on="{ ...on, ...tooltipOn }"
                                    class="d-flex align-center w-100"
                                >
                                    <v-list-item-icon>
                                        <!-- Regular question icons without wrapper -->
                                        <component
                                            :is="item.icon"
                                            width="30"
                                            height="30"
                                        />
                                    </v-list-item-icon>
                                    <v-list-item-content :style="`max-width: ${item.disabled ? (item.disabledLabel === 'Conditioned' ? '460px' : '500px') : '533px'}; overflow: hidden;`">
                                        <v-list-item-title 
                                            class="destination-selector__question-title"
                                            style="display: flex; align-items: center; overflow: hidden;"
                                        >
                                            <!-- Regular questions - location part lighter -->
                                            <span 
                                                class="mr-2 grey_700--text destination-selector__question-location"
                                                style="flex-shrink: 0;"
                                            >{{ item.formattedLocation }}:</span>
                                            <span 
                                                class="destination-selector__question-text"
                                                :style="`overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: ${item.disabled ? (item.disabledLabel === 'Hidden' ? '330px' : '340px') : '433px'};`"
                                            >{{ item.title }}</span>
                                        </v-list-item-title>
                                    </v-list-item-content>
                                    <v-list-item-action 
                                        v-if="item.disabled"
                                        style="flex-shrink: 0; min-width: 70px;"
                                    >
                                        <span class="caption text--disabled">{{ item.disabledLabel }}</span>
                                    </v-list-item-action>
                                </div>
                            </template>
                            <span class="tooltip-text">{{ item.formattedLocation }}: {{ item.title }}</span>
                        </v-tooltip>

                        <!-- No tooltip for special options -->
                        <template v-else>
                            <v-list-item-icon>
                                <!-- Special icons with styled wrapper -->
                                <div 
                                    :class="[
                                        'destination-icon',
                                        'destination-icon--special'
                                    ]"
                                    style="background-color: #F2F5FF; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; border-radius: 8px; padding: 7px; box-sizing: border-box;"
                                >
                                    <span v-if="item.isEmoji" class="destination-icon__emoji">{{ item.icon }}</span>
                                    <component
                                        v-else
                                        :is="item.icon"
                                        width="16"
                                        height="16"
                                    />
                                </div>
                            </v-list-item-icon>
                            <v-list-item-content :style="`max-width: ${item.disabled ? (item.disabledLabel === 'Conditioned' ? '460px' : '500px') : '533px'}; overflow: hidden;`">
                                <v-list-item-title 
                                    class="destination-selector__question-title"
                                    style="display: flex; align-items: center; overflow: hidden;"
                                >
                                    <!-- Special options - all text same color -->
                                    <span 
                                        class="destination-selector__question-text"
                                        :style="`overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: ${item.disabled ? (item.disabledLabel === 'Hidden' ? '330px' : '340px') : '433px'};`"
                                    >{{ item.label }}</span>
                                </v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action 
                                v-if="item.disabled"
                                :style="`flex-shrink: 0; min-width: 70px; margin-left: ${item.disabledLabel === 'Conditioned' ? '24px' : '16px'};`"
                            >
                                <span class="caption text--disabled">{{ item.disabledLabel }}</span>
                            </v-list-item-action>
                        </template>
                    </v-list-item>
                </template>
            </f-select>
        </div>
    </div>
</template>

<script>
    import { ref, computed, watch } from '@vue/composition-api'
    import { FieldsEnumerationGettersUtils } from '@/store/util_modules/fields_enumeration_getters'

    export default {
        name: "DestinationSelector",
        components: {
            LogOutIcon: () => import('vue-feather-icons/icons/LogOutIcon'),
            ExternalLinkIcon: () => import('vue-feather-icons/icons/ExternalLinkIcon')
        },
        props: {
            option: {
                type: Object,
                required: true
            },
            currentFieldId: {
                type: String,
                required: true
            },
            destination: {
                type: Object,
                default: () => ({
                    type: 'question',
                    field_id: undefined
                })
            },
            alreadySelectedFieldIds: {
                type: Array,
                default: () => []
            }
        },
        setup(props, { emit, root }) {
            console.log(`🏗️ DestinationSelector for option ${props.option.index}, disabled IDs:`, props.alreadySelectedFieldIds)
            console.log(`🔗 Initial destination data:`, props.destination)
            console.log(`🌐 Initial redirect_url value:`, props.destination.redirect_url)
            
            // Estado del componente
            const selectedDestination = ref(getInitialDestination())
            
            // Initialize redirect URL, but ignore placeholder values
            let initialUrl = props.destination.redirect_url || ''
            // If the URL is just the placeholder, treat it as empty
            if (initialUrl === 'https://example.com') {
                initialUrl = ''
                console.log('🚫 Detected placeholder URL, treating as empty')
            }
            const redirectUrl = ref(initialUrl)
            console.log(`📝 Final initialized redirectUrl:`, redirectUrl.value)
            
            const urlError = ref(false)
            const menuOpen = ref(false)
            
            // Template ref for URL input
            const urlInput = ref(null)
            
            // Only mark as touched if we're initializing with redirect_url AND it has a value
            // If it's empty, don't mark as touched initially
            const urlFieldTouched = ref(
                getInitialDestination() === 'redirect_url' && initialUrl.trim() !== ''
            )
            console.log(`🎯 Initial urlFieldTouched state:`, urlFieldTouched.value)
            
            // Flag to prevent immediate validation on component mount
            const isInitialLoad = ref(true)
            
            // Set initial load to false after component is mounted
            root.$nextTick(() => {
                setTimeout(() => {
                    isInitialLoad.value = false
                    console.log('✅ Initial load completed, validation now active')
                    
                    // If redirect_url is initially selected, start watching for input
                    if (selectedDestination.value === 'redirect_url') {
                        console.log('🔗 Initial redirect URL detected, starting input watcher...')
                        startWatchingForInput()
                    }
                }, 100)
            })

            // Computed property to check if redirect URL is invalid
            const isRedirectUrlInvalid = computed(() => {
                if (selectedDestination.value === 'redirect_url') {
                    const url = redirectUrl.value
                    const isEmpty = !url || url.trim() === ''
                    const isPlaceholder = url === 'https://example.com'
                    const isInvalid = isEmpty || isPlaceholder
                    
                    console.log(`🔍 URL validation check:`, {
                        url,
                        isEmpty,
                        isPlaceholder,
                        isInvalid,
                        touched: urlFieldTouched.value,
                        isInitialLoad: isInitialLoad.value
                    })
                    
                    // Only show red border if field is invalid AND user has touched it AND not initial load
                    return isInvalid && urlFieldTouched.value && !isInitialLoad.value
                }
                return false
            })

            // Keep menu open when redirect URL is selected
            const menuProps = computed(() => ({
                nudgeBottom: 46,
                contentClass: 'conditional-logic-editor__target-items'
            }))

            // Initialize destination based on props
            function getInitialDestination() {
                if (props.destination.type === 'end_survey') return 'end_survey'
                if (props.destination.type === 'thank_you') return 'thank_you'
                if (props.destination.type === 'redirect_url') return 'redirect_url'
                return props.destination.field_id
            }

            // Handle click on the redirect field to open dropdown
            const openMenu = () => {
                console.log('🖱️ Redirect field clicked, opening menu')
                
                // Set the menu to open
                menuOpen.value = true
                
                // Ensure the activator is properly positioned
                root.$nextTick(() => {
                    const activator = root.$refs.menuActivator
                    if (activator) {
                        console.log('📍 Menu activator positioned')
                    }
                })
            }

            // Handle change from hidden select
            const selectDestination = (item) => {
                console.log('🔄 Destination selected:', item)
                selectedDestination.value = item.value
                menuOpen.value = false
                
                // If redirect_url is selected, start watching for input to become available
                if (item.value === 'redirect_url') {
                    console.log('🔗 Redirect URL selected, starting input watcher...')
                    startWatchingForInput()
                }
            }

            // Utility function to focus URL input
            const focusUrlInput = () => {
                console.log('🎯 Attempting to focus URL input...')
                console.log('📍 URL input ref:', urlInput.value)
                console.log('📍 All refs available:', {
                    urlInput: urlInput.value,
                    fallbackRef: root.$refs.urlInput
                })
                
                if (urlInput.value) {
                    console.log('✅ URL input found via ref, focusing...')
                    urlInput.value.focus()
                    console.log('🎯 Focus called on input')
                    return true
                } else if (root.$refs.urlInput) {
                    console.log('✅ URL input found via $refs fallback, focusing...')
                    root.$refs.urlInput.focus()
                    console.log('🎯 Focus called on input (fallback)')
                    return true
                } else {
                    console.log('❌ URL input ref not found in either location')
                    return false
                }
            }

            // Watch specifically for when redirect URL input becomes available
            const urlInputWatcher = ref(null)

            // Start watching for URL input when redirect_url is selected
            const startWatchingForInput = () => {
                console.log('👀 Starting to watch for URL input availability')
                let attempts = 0
                const maxAttempts = 20 // 2 seconds max
                
                urlInputWatcher.value = setInterval(() => {
                    attempts++
                    console.log(`🔄 Attempt ${attempts} to focus URL input`)
                    
                    if (focusUrlInput()) {
                        // Success! Stop watching
                        clearInterval(urlInputWatcher.value)
                        urlInputWatcher.value = null
                        console.log('✅ Successfully focused URL input!')
                    } else if (attempts >= maxAttempts) {
                        // Give up after max attempts
                        clearInterval(urlInputWatcher.value)
                        urlInputWatcher.value = null
                        console.log('❌ Failed to focus URL input after max attempts')
                    }
                }, 100) // Check every 100ms
            }

            // Handle focus event on URL input (reset touched state)
            const handleUrlFocus = () => {
                console.log('🎯 URL field focused - resetting touched state')
                urlFieldTouched.value = false
            }

            // Handle blur event on URL input
            const handleUrlBlur = () => {
                console.log('👋 URL field lost focus')
                urlFieldTouched.value = true
            }

            // Reset touched state when destination changes
            watch(selectedDestination, (newValue, oldValue) => {
                console.log(`🔄 Destination changed from ${oldValue} to ${newValue}`)
                
                if (newValue === 'redirect_url') {
                    // When selecting redirect_url, don't mark as touched initially
                    // Only mark as touched if we had a previous value and we're switching back
                    if (oldValue !== 'redirect_url') {
                        urlFieldTouched.value = false
                        console.log('🎯 Reset touched state for new redirect URL selection')
                    }
                    
                    // Start watching for input availability instead of immediate focus
                    console.log('🔗 Starting input watcher from destination change...')
                    startWatchingForInput()
                } else {
                    // Clear URL when switching away from redirect_url
                    redirectUrl.value = ''
                    urlFieldTouched.value = false // Reset touched state
                    console.log('🧹 Cleared URL and reset touched state')
                    
                    // Stop watching if we were watching
                    if (urlInputWatcher.value) {
                        clearInterval(urlInputWatcher.value)
                        urlInputWatcher.value = null
                        console.log('⏹️ Stopped watching for URL input')
                    }
                }
            })

            // Campos disponibles para saltar (excluyendo el campo actual)
            const availableFields = computed(() => {
                try {
                    const enumeratedList = root.$store.getters['edit/' + FieldsEnumerationGettersUtils.enumeratedFieldsListGetterName]
                    
                    if (enumeratedList && enumeratedList.length > 0) {
                        return enumeratedList
                            .filter(({fieldId}) => fieldId !== props.currentFieldId)
                            .map(field => ({
                                value: field.fieldId,
                                label: `${field.formattedLocation}: ${field.fieldObj.title}`,
                                formattedLocation: field.formattedLocation,
                                title: field.fieldObj.title,
                                icon: field.fieldConst.ICON_COMPONENT,
                                type: 'question',
                                disabled: props.alreadySelectedFieldIds.includes(field.fieldId) || field.fieldObj.properties.hide,
                                disabledLabel: getFieldDisabledLabel(field)
                            }))
                    }
                    
                    return []
                } catch (error) {
                    console.warn('Error getting fields:', error)
                    return []
                }
            })

            // Special destination options
            const specialOptions = computed(() => [
                {
                    value: 'end_survey',
                    label: 'End of survey',
                    icon: 'LogOutIcon',
                    type: 'special',
                    disabled: false,
                    disabledLabel: null
                },
                {
                    value: 'redirect_url', 
                    label: 'Redirect URL',
                    icon: 'ExternalLinkIcon',
                    type: 'special',
                    disabled: false,
                    disabledLabel: null
                },
                {
                    value: 'thank_you',
                    label: 'Thank you page',
                    icon: '🎉',
                    type: 'special',
                    disabled: false,
                    disabledLabel: null,
                    isEmoji: true
                }
            ])

            // All destination options (questions + special options)
            const allDestinationOptions = computed(() => [
                ...availableFields.value,
                ...specialOptions.value
            ])

            // Validate URL format
            const validateUrl = () => {
                if (!redirectUrl.value) {
                    urlError.value = false
                    return
                }
                
                try {
                    new URL(redirectUrl.value)
                    urlError.value = false
                } catch {
                    urlError.value = true
                }
            }

            // Helper function for disabled labels
            function getFieldDisabledLabel(field) {
                if (field.fieldObj.properties.hide) {
                    return "Hidden"
                }
                if (props.alreadySelectedFieldIds.includes(field.fieldId)) {
                    return "Conditioned"
                }
                return "Disabled"
            }

            // Watch para cambios en el destino seleccionado
            watch(selectedDestination, (newDestination) => {
                console.log(`📋 Destination selected in option ${props.option.index}:`, newDestination)
                
                let destinationData = {}
                let isValid = true
                
                if (newDestination === 'end_survey') {
                    destinationData = {
                        type: 'end_survey'
                    }
                } else if (newDestination === 'thank_you') {
                    destinationData = {
                        type: 'thank_you'
                    }
                } else if (newDestination === 'redirect_url') {
                    destinationData = {
                        type: 'redirect_url',
                        redirect_url: redirectUrl.value
                    }
                    // Validation: redirect_url is invalid if URL is empty or placeholder
                    const url = redirectUrl.value
                    const isEmpty = !url || url.trim() === ''
                    const isPlaceholder = url === 'https://example.com'
                    isValid = !isEmpty && !isPlaceholder
                    
                    console.log(`🎯 Redirect URL validation:`, {
                        url,
                        isEmpty,
                        isPlaceholder,
                        isValid
                    })
                } else if (newDestination) {
                    // Regular question
                    destinationData = {
                        type: 'question',
                        field_id: newDestination
                    }
                }
                
                if (Object.keys(destinationData).length > 0) {
                    const updateData = {
                        option: props.option,
                        destination: destinationData,
                        isValid: isValid
                    }
                    console.log('📤 Emitting update-destination:', updateData)
                    emit('update-destination', updateData)
                }
            })

            // Watch para cambios en la URL de redirección
            watch(redirectUrl, (newUrl) => {
                if (selectedDestination.value === 'redirect_url') {
                    validateUrl()
                    
                    // Check if URL is valid (not empty, not placeholder, and not just whitespace)
                    const isEmpty = !newUrl || newUrl.trim() === ''
                    const isPlaceholder = newUrl === 'https://example.com'
                    const isValid = !isEmpty && !isPlaceholder
                    
                    console.log(`🌐 URL change validation:`, {
                        newUrl,
                        isEmpty,
                        isPlaceholder,
                        isValid
                    })
                    
                    const updateData = {
                        option: props.option,
                        destination: {
                            type: 'redirect_url',
                            redirect_url: newUrl
                        },
                        isValid: isValid
                    }
                    console.log('📤 Emitting URL update:', updateData, 'Valid:', isValid)
                    emit('update-destination', updateData)
                }
            })

            // New function to determine if a tooltip should be shown
            const shouldShowTooltip = (item) => {
                // Only show tooltip for questions (not special options)
                if (item.type === 'special') {
                    return false
                }
                
                // Construct the full text as it appears in the dropdown
                const fullText = `${item.formattedLocation}: ${item.title}`
                
                // Show tooltip if the text is longer than a reasonable threshold
                // This threshold accounts for the dropdown width, icon space, and status label space
                // Updated threshold for the increased width (360px for text)
                const maxCharacters = 60 // Increased from 45 to account for additional 90px width
                
                return fullText.length > maxCharacters
            }

            // Dynamic placeholder based on option
            const dynamicPlaceholder = computed(() => {
                if (props.option && props.option.index !== undefined) {
                    const letter = String.fromCharCode(65 + props.option.index) // A, B, C, etc.
                    return `Select a destination for ${letter}`
                }
                return 'Select a destination'
            })

            // Option letter for displaying in selection
            const optionLetter = computed(() => {
                if (props.option && props.option.index !== undefined) {
                    const letter = String.fromCharCode(65 + props.option.index) // A, B, C, etc.
                    
                    // Special handling for star rating emoji options
                    if (props.option.isEmoji && props.option.emoji) {
                        return `${letter} - ${props.option.emoji}`
                    }
                    
                    // Special handling for star rating with stars (non-emoji)
                    // If the option text is just a number (like "1", "2", etc.), it's likely a star rating
                    if (props.option.text && /^\d+$/.test(props.option.text)) {
                        return `${letter} - ${props.option.text}`
                    }
                    
                    return letter
                }
                return ''
            })

            return {
                selectedDestination,
                allDestinationOptions,
                redirectUrl,
                urlError,
                validateUrl,
                menuProps,
                menuOpen,
                openMenu,
                selectDestination,
                isRedirectUrlInvalid,
                handleUrlFocus,
                handleUrlBlur,
                urlInput,
                shouldShowTooltip,
                dynamicPlaceholder,
                optionLetter
            }
        }
    }
</script>

<style lang="scss" scoped>
    .destination-selector {
        display: flex;
        flex-direction: column;
        flex: 1;

        &__wrapper {
            display: flex;
            flex-direction: column;
        }

        &__question {
            flex: 1;
            min-width: 300px;
        }

        &__redirect-field {
            position: relative;
            flex: 1;
            min-width: 300px;
        }

        &__field-wrapper {
            display: flex;
            align-items: center;
            padding: 6px 12px;
            border: 1px solid rgba(0, 0, 0, 0.12);
            border-radius: 8px;
            cursor: pointer;
            min-height: 42px;
            height: 42px;
            
            &:hover {
                border-color: rgba(0, 0, 0, 0.24);
            }

            &--error {
                border-color: #f44336 !important;
                
                &:hover {
                    border-color: #d32f2f !important;
                }
            }
        }

        &__content {
            display: flex;
            align-items: center;
            flex: 1;
        }

        &__url-input {
            flex: 1;
            border: none;
            outline: none;
            background: transparent;
            font-size: 0.875rem;
            color: var(--v-bluish_gray_700-base);
            margin-left: 4px;
            cursor: text;
            position: relative;
            z-index: 2;
            padding: 2px 4px;
            height: 20px;
            line-height: 20px;
            
            &::placeholder {
                color: var(--v-bluish_gray_500-base);
                opacity: 0.7;
            }
            
            &:focus {
                outline: none;
                background: rgba(255, 255, 255, 0.9);
                border-radius: 4px;
            }

            &--error {
                &::placeholder {
                    color: #f44336;
                    opacity: 0.8;
                }
            }
        }

        &__dropdown-arrow {
            margin-left: 8px;
            color: rgba(0, 0, 0, 0.54);
            display: flex;
            align-items: center;
            
            &:hover {
                color: rgba(0, 0, 0, 0.87);
            }
        }

        // Estilos para el f-select
        ::v-deep .v-input__control {
            .v-input__slot {
                background-color: transparent !important;
                box-shadow: none !important;
                border: none !important;
                border-radius: 8px !important;
                .v-select__slot .v-select__selections {
                    .v-select__selection {
                        font-weight: 400;
                        font-size: 0.875rem;
                        color: var(--v-bluish_gray_700-base);
                        max-width: 100%;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                }
            }
        }

        // Estilos para los iconos de destino
        .destination-icon {
            width: 30px;
            height: 30px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 8px;
            
            &--special {
                background-color: #F2F5FF !important;
                width: 30px !important;
                height: 30px !important;
                min-width: 30px !important;
                min-height: 30px !important;
                padding: 0 !important;
                margin: 0 !important;
                box-sizing: border-box !important;
            }

            &__emoji {
                font-size: 16px;
                line-height: 1;
            }
        }

        // ESTILOS PRINCIPALES PARA TRUNCAMIENTO - MUY ESPECÍFICOS
        ::v-deep .conditional-logic-editor__target-items {
            max-height: 300px !important;
            overflow-y: auto !important;
            min-width: 400px !important; // Ancho mínimo del menú
            max-width: 500px !important; // Ancho máximo del menú
            
            .v-list {
                padding: 4px 0 !important;
                width: 100% !important;
                
                .v-list-item {
                    min-height: 40px !important;
                    padding: 8px 16px !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: space-between !important;
                    width: 100% !important;
                    box-sizing: border-box !important;
                    
                    .v-list-item__icon {
                        flex: 0 0 40px !important;
                        margin-right: 12px !important;
                        margin-left: 0 !important;
                    }

                    .v-list-item__content {
                        flex: 1 1 auto !important;
                        min-width: 0 !important;
                        max-width: 280px !important; // Límite más específico
                        overflow: hidden !important;
                        margin-right: 8px !important;
                        
                        .v-list-item__title {
                            overflow: hidden !important;
                            text-overflow: ellipsis !important;
                            white-space: nowrap !important;
                            max-width: 100% !important;
                            width: 100% !important;
                            line-height: 1.2 !important;
                            display: block !important; // Forzar display block para truncamiento
                            
                            &.destination-selector__question-title {
                                display: flex !important;
                                align-items: center !important;
                                overflow: hidden !important;
                                max-width: 100% !important;
                                
                                .destination-selector__question-location {
                                    flex: 0 0 auto !important;
                                    margin-right: 8px !important;
                                    white-space: nowrap !important;
                                }
                                
                                .destination-selector__question-text {
                                    flex: 1 1 auto !important;
                                    min-width: 0 !important;
                                    overflow: hidden !important;
                                    text-overflow: ellipsis !important;
                                    white-space: nowrap !important;
                                    max-width: 200px !important; // Límite físico en píxeles
                                }
                            }
                        }
                    }

                    .v-list-item__action {
                        flex: 0 0 auto !important;
                        margin-left: 16px !important;
                        min-width: 70px !important;
                        max-width: 90px !important;
                        align-self: center !important;
                        
                        .caption {
                            white-space: nowrap !important;
                            font-size: 0.75rem !important;
                            padding: 2px 6px !important;
                            border-radius: 4px !important;
                            background-color: rgba(0, 0, 0, 0.06) !important;
                            text-align: center !important;
                        }
                    }
                }
                
                .v-list-item--dense {
                    min-height: 36px !important;
                }
            }
            
            // ICONOS ESPECIALES CON BACKGROUND
            .destination-icon--special {
                background-color: #F2F5FF !important;
            }
        }

        // Estilos para los tooltips
        .tooltip-text {
            font-size: 0.875rem;
            line-height: 1.4;
            max-width: 300px;
            white-space: normal;
            word-wrap: break-word;
        }

        // Asegurar que los tooltips tengan suficiente z-index
        ::v-deep .v-tooltip__content {
            z-index: 9999 !important;
            background-color: rgba(97, 97, 97, 0.95) !important;
            border-radius: 4px !important;
            padding: 8px 12px !important;
        }

        // APLICAR BACKGROUND A ICONOS ESPECIALES EN TODOS LOS MENÚS
        ::v-deep .v-menu__content {
            .destination-icon--special {
                background-color: #F2F5FF !important;
            }
        }

        ::v-deep .v-list {
            .destination-icon--special {
                background-color: #F2F5FF !important;
            }
        }
    }
</style> 